import { TStore } from '@components/types';
import { getSafeUrl, sanityIoImageLoader } from '@core/utils';
import { BadgeCheck, Bike, MapPin, ShoppingBag, Star } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

const formatDistance = (meters: number) =>
    meters < 1000 ? `${Math.round(meters)} m` : meters < 10000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters / 1000)} km`;

// Same destinations as the compact card: hotels under /stays, shops under /shop, everything else /store.
const storeHref = (store: TStore) =>
    `/${store?.category_id == 1 && store?.storemeta?.functions?.booking ? 'stays' : store?.category_id == 1 ? 'shop' : 'store'}/${store?.id}/${getSafeUrl(store?.name)}`;

type Props = {
    store: TStore;
    // true: delivers to the visitor's area; false: does not; undefined: not checked (no status is shown).
    isAvailable?: boolean;
    priority?: boolean;
};

// Card for store listing pages (full width of its grid cell, unlike the compact carousel card).
export const StoreListCard: React.FC<Props> = ({ store, isAvailable, priority = false }) => {
    const href = storeHref(store);
    const rating = store?.stats?.rating_overall;
    const distance = store?.address?.distance;
    const place = [store?.address?.locality || store?.address?.street_name, store?.address?.city?.name].filter(Boolean).join(', ');

    return (
        <article className="group relative flex gap-4 rounded-2xl bg-white border border-solid border-neutral-100 shadow-sm p-3 hover:shadow-lg hover:border-neutral-200 transition-shadow">
            {/* Whole card is the link; the place link below sits above it. */}
            <Link href={href} prefetch={false} aria-label={store?.name} className="absolute inset-0 z-0 rounded-2xl" />

            <div className={`relative flex-shrink-0 w-28 h-28 md:w-32 md:h-32 rounded-xl overflow-hidden bg-neutral-100 ${isAvailable === false ? 'grayscale' : ''}`}>
                <Image
                    loader={sanityIoImageLoader}
                    src={store?.image || 'https://cdn.jhattse.com/public/consumer/placeholder/store.png'}
                    alt={store?.name || 'Store'}
                    fill
                    sizes="128px"
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    loading={priority ? 'eager' : 'lazy'}
                />
            </div>

            <div className="relative z-10 pointer-events-none flex flex-col min-w-0 flex-1 gap-1.5 py-0.5">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h3 className="m-0 text-base font-bold text-neutral-900 leading-tight line-clamp-2 font-manrope">{store?.name}</h3>
                        {store?.category?.name && <p className="m-0 mt-0.5 text-xs font-medium text-neutral-500">{store.category.name}</p>}
                    </div>
                    {rating > 0 && (
                        <span className="inline-flex items-center gap-1 flex-shrink-0 rounded-lg bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white">
                            {Number(rating).toFixed(1)} <Star className="w-3 h-3 fill-white" />
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                    {store?.verified && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 px-2 py-0.5 text-[11px] font-semibold">
                            <BadgeCheck className="w-3 h-3" /> Trusted
                        </span>
                    )}
                    {store?.is_delivery && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 px-2 py-0.5 text-[11px] font-semibold">
                            <Bike className="w-3 h-3" /> Delivery
                        </span>
                    )}
                    {store?.is_pickup && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 px-2 py-0.5 text-[11px] font-semibold">
                            <ShoppingBag className="w-3 h-3" /> Pickup
                        </span>
                    )}
                    {!(rating > 0) && <span className="text-[11px] text-neutral-400">New</span>}
                </div>

                {place && (
                    <p className="m-0 flex items-center gap-1 text-xs text-neutral-500 min-w-0">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-brand-500" />
                        <span className="truncate">{place}</span>
                        {distance != undefined && distance < 100000 && (
                            <span className="flex-shrink-0 font-semibold text-neutral-700">· {formatDistance(distance)}</span>
                        )}
                    </p>
                )}

                {isAvailable !== undefined && (
                    <p className={`m-0 mt-auto text-xs font-semibold ${isAvailable ? 'text-emerald-700' : 'text-neutral-400'}`}>
                        {isAvailable ? 'Delivers to your area' : 'Not delivering to your area right now'}
                    </p>
                )}
            </div>
        </article>
    );
};
